if (!customElements.get('td-locked-status')) {
  class TdLockedStatus extends HTMLElement {
    static SELECTORS = Object.freeze({
      banner: '[data-td-locked-banner]',
      title: '[data-td-locked-title]',
      subtitle: '[data-td-locked-subtitle]',
      loginHelper: '[data-td-locked-login-helper]',
      addToCart: '[data-td-locked-atc]',
      payment: '[data-td-locked-payment]',
      stickyAddToCart: '[data-td-locked-sticky]'
    });

    connectedCallback() {
      this.form = this.closest('form');
      // The variant picker is a sibling of the product form in vendor markup.
      this.product = this.closest('.js-product');
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.submitHandler = this.handleSubmit.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.stickySubmitHandler = this.handleStickySubmit.bind(this);
      this.variantData = this.parseDataAttribute('variants', []);
      this.copy = this.parseDataAttribute('copy', {});
      this.isMember = this.dataset.tdIsMember === 'true';
      this.variantStatuses = this.buildVariantStatuses(this.variantData);

      this.product?.addEventListener('on:variant:change', this.variantChangeHandler);
      this.product?.addEventListener('sticky-cta-add', this.stickySubmitHandler, true);
      this.form?.addEventListener('submit', this.submitHandler, true);
      this.form?.addEventListener('click', this.clickHandler, true);

      const activeVariant = this.form?.querySelector('[name="id"]')?.value;
      this.update(activeVariant);
    }

    disconnectedCallback() {
      this.product?.removeEventListener('on:variant:change', this.variantChangeHandler);
      this.product?.removeEventListener('sticky-cta-add', this.stickySubmitHandler, true);
      this.form?.removeEventListener('submit', this.submitHandler, true);
      this.form?.removeEventListener('click', this.clickHandler, true);
      this.form = null;
      this.product = null;
    }

    parseDataAttribute(name, fallback) {
      try {
        return JSON.parse(this.dataset[`td${name.charAt(0).toUpperCase()}${name.slice(1)}`] || '') || fallback;
      } catch (error) {
        console.error(`Unable to parse locked-status ${name} data`, error);
        return fallback;
      }
    }

    buildVariantStatuses(variants) {
      const colorStatuses = new Map();
      variants.forEach((variant) => {
        if (variant.status && variant.color && !colorStatuses.has(variant.color)) {
          colorStatuses.set(variant.color, variant.status);
        }
      });

      return new Map(
        variants.map((variant) => [
          String(variant.id),
          variant.status || colorStatuses.get(variant.color) || null
        ])
      );
    }

    handleVariantChange(event) {
      if (event.detail?.form !== this.form) return;

      if (!event.detail.variant) {
        this.update(null);
        return;
      }

      this.update(event.detail.variant.id);
    }

    handleSubmit(event) {
      if (this.isPurchaseLocked) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    handleClick(event) {
      if (
        this.isPurchaseLocked
        && event.target instanceof Element
        && event.target.closest(TdLockedStatus.SELECTORS.addToCart)
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    handleStickySubmit(event) {
      if (this.isPurchaseLocked) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    update(variantId) {
      const status = variantId ? this.variantStatuses.get(String(variantId)) : null;
      const isLockedToAll = status === 'Locked to all';
      const isTemporarilyMemberLocked = status === 'Locked to non-Janji Collective members - Temporarily';
      const isMemberLocked = status === 'Locked to non-Janji Collective members';
      const isMemberOnlyLock = isTemporarilyMemberLocked || isMemberLocked;
      const canPurchase = !isLockedToAll && (!isMemberOnlyLock || this.isMember);
      const showBanner = isLockedToAll || (isMemberOnlyLock && !this.isMember);

      this.isPurchaseLocked = !canPurchase;
      this.hidden = !showBanner;
      this.updateCopy(status, isTemporarilyMemberLocked, isMemberLocked);
      this.toggleElements(!canPurchase, showBanner, isMemberOnlyLock && !this.isMember);
    }

    updateCopy(status, isTemporarilyMemberLocked, isMemberLocked) {
      const copy = isTemporarilyMemberLocked
        ? this.copy.temporary
        : isMemberLocked
          ? this.copy.members
          : this.copy.all;
      const title = this.querySelector(TdLockedStatus.SELECTORS.title);
      const subtitle = this.querySelector(TdLockedStatus.SELECTORS.subtitle);

      if (!status || !copy) {
        if (title) title.textContent = '';
        if (subtitle) subtitle.textContent = '';
        return;
      }

      if (title) title.textContent = copy.title || '';
      if (subtitle) subtitle.textContent = this.isMember ? copy.memberSubtitle || copy.subtitle || '' : copy.subtitle || '';
    }

    toggleElements(hidePurchaseControls, showBanner, showLoginHelper) {
      this.toggleHidden(this.form?.querySelector(TdLockedStatus.SELECTORS.addToCart), hidePurchaseControls);
      this.toggleHidden(this.form?.querySelector(TdLockedStatus.SELECTORS.payment), hidePurchaseControls);
      this.toggleHidden(this.product?.querySelector(TdLockedStatus.SELECTORS.stickyAddToCart), hidePurchaseControls);
      this.toggleHidden(this.querySelector(TdLockedStatus.SELECTORS.loginHelper), !showLoginHelper);
      this.hidden = !showBanner;
    }

    toggleHidden(element, hidden) {
      if (!element) return;
      element.classList.toggle('hidden', hidden);
      element.toggleAttribute('hidden', hidden);
    }
  }

  customElements.define('td-locked-status', TdLockedStatus);
}
